import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createSlots as _createSlots, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "q-my-none" }

import type { PropType } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { QFile, QRejectedEntry, QSelect, useQuasar } from 'quasar';
import * as tus from 'tus-js-client'

// Stores
import { useUiStore } from 'src/stores/ui';
import { useApiStore } from 'src/stores/api';
import { useSecurityStore } from 'src/stores/security';

// Personal components
import QBtnValid from '@components/form/QBtnValid.vue';
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';
import QProgressBar from '@components/QProgressBar.vue';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// Personal libs
import { sanitizeFileName, tailleFichier } from '@shared/lib/tools.lib';
import { IProgram, IUser } from '@shared/interface/model.interface';
import { videoFileRules, requiredAtLeastOneDemonstrator } from '@shared/lib/form.lib';
import { UserStatusEnum } from '@shared/enum/user.enum';
import videoRepository from '@data/repository/video.repository';
import { IApiPostVideo } from '@data/datasource/remote/video.api';
import {
  displayDialogError,
  displayNotifSuccess,
  displayNotifWarning,
} from '@shared/lib/notify.lib';
import { IQSelectOption } from '@shared/interface/quasar.interface';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';
import { extractExtension, startOrResumeUpload } from '@data/datasource/remote/common/api.upload';
import { onUploadProgressTus } from '@data/datasource/remote/common/api.lib';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { ApiError } from '@shared/error/api.error';
import { ApiErrorLabel } from '@shared/enum/api-error-label.enum';
import { logger } from '@shared/lib/logger/logger-manager';
import { AuthorizedFileFormatEnum } from '@shared/enum/authorized-file-format.enum';
import { FormNameEnum } from '@shared/enum/form-name.enum';

/**
 * Static variables
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DialogVideoUpload',
  props: {
  show: { type: Boolean, required: true, default: false },
  program: { type: Object as PropType<IProgram>, required: true },
},
  emits: ['close', 'success', 'failed'],
  setup(__props, { emit: __emit }) {

// Vendor
const defaultVideoFormat = [AuthorizedFileFormatEnum.MP4];

/**
 * Elements ref
 */
const videoUploaderRef = ref<QFile>();
const pratiquantSelectRef = ref<QSelect>();

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * Events
 */
const emits = __emit;

/**
 * Stores
 */
const uiStore = useUiStore();
const apiStore = useApiStore();
const securityStore = useSecurityStore();

/**
 * Global
 */
const $q = useQuasar()

/**
 * Properties
 */
const props = __props;
const { show, program } = toRefs(props);

/**
 * Datas
 */
const loading = ref<boolean>(false);
const selectedFile = ref<File | null>(null);
const selectedPrimaryUser = ref<IQSelectOption<IUser> | null>(null);
const selectedSecondaryUsers = ref<IQSelectOption<IUser>[] | null>(null);
const activeUsers = ref<IQSelectOption<IUser>[]>([]);
const activeSecondaryUsers = ref<IQSelectOption<IUser>[]>([]);
const error = ref<string | undefined>(undefined);
const apiUploadController = ref<tus.Upload | undefined>(undefined)
const abortController = ref<AbortController | undefined>(undefined)

/**
 * Computed properties
 */
const loaded = computed<number | undefined>(() => apiStore.uploadLoaded);
const total = computed<number | undefined>(() => apiStore.uploadTotal);
const users = computed<IUser[]>(() => uiStore.users ?? []);
const storeConfig = computed({
  get: () => uiStore.config,
  set: (value) => (uiStore.config = value),
});
const maxFileSize = computed<number>(() => {
  return storeConfig.value?.uploader?.maxFileSize != null
    ? storeConfig.value.uploader.maxFileSize
    : 0;
});
const authorizedFileFormat = computed<string | undefined>(() => storeConfig?.value?.uploader?.fileTypes?.join(',') ?? defaultVideoFormat.join(','));

/**
 * Methods
 */
function onUpdateModelValue() {
  videoUploaderRef.value?.resetValidation();
  pratiquantSelectRef.value?.resetValidation();
}

function onRejected(rejectedEntries: QRejectedEntry[]) {
  setQFieldCounter(rejectedEntries[0].file.size ?? 0);
}

function setQFieldCounter(newValue: number) {
  const erreurEl = document.getElementsByClassName('q-field__counter');
  if (erreurEl != null && erreurEl.length > 0) {
    erreurEl[0].innerHTML = counterLabelFn({
      totalSize: tailleFichier(newValue),
    });
  }
}

function counterLabelFn({ totalSize }) {
  return `Taille du fichier : ${totalSize}`;
}

function onPauseUpload() {
  abortController.value?.abort()
  apiUploadController.value?.abort()
  loading.value = false
}

function onClose() {
  onPauseUpload()
  clearForm()
  resetUpload()
  emits('close');
}

function clearForm() {
  selectedFile.value = null;
  selectedPrimaryUser.value = null
  selectedSecondaryUsers.value = null
}

function onClearQFile() {
  selectedFile.value = null;
  videoUploaderRef.value?.resetValidation();

  setQFieldCounter(0);
}

function resetUpload() {
  // Reset uploader
  apiUploadController.value = undefined

  // Reset loader
  loading.value = false

  // Reset progress bar
  apiStore.uploadLoaded = undefined;
  apiStore.uploadTotal = undefined;
}

async function onValidate() {
  videoUploaderRef.value?.validate();
  pratiquantSelectRef.value?.validate();

  if (videoUploaderRef.value?.hasError || pratiquantSelectRef.value?.hasError) {
    return;
  } else {
    videoUploaderRef.value?.resetValidation();
    pratiquantSelectRef.value?.resetValidation();

    if (selectedFile.value != null && selectedPrimaryUser.value != null) {
      loading.value = true;

      let params: IApiPostVideo = {
        videoFile: selectedFile.value,
        videoFileName: sanitizeFileName(selectedFile.value.name, true),
        videoFileSize: selectedFile.value.size,
        videoFileExtension: extractExtension(selectedFile.value.name),
        programId: program.value.id ?? 0,
        mainUserId: selectedPrimaryUser.value?.value?.id ?? 0,
      };

      if (
        selectedSecondaryUsers.value != null &&
        selectedSecondaryUsers.value.length > 0
      ) {
        params.secondaryUsersId = selectedSecondaryUsers.value
          .map((u) => u.value?.id ?? 0)
          .filter((u) => u !== 0);
      }

      videoRepository
        .postUploadVideo({
          file: selectedFile.value,
          newFileName: params.videoFileName,
          onProgress: (bytesUploaded: number, bytesTotal: number) => {
            onUploadProgressTus(bytesUploaded, bytesTotal)
          },
          onError: (error: Error | ApiError | tus.DetailedError) => {
            let errorMessage = 'Erreur inconnue'
            if (error instanceof tus.DetailedError) {
              const response  = JSON.parse(error.originalResponse?.getBody() ?? "{}") as IApiResponse<string, string>
              if ('returnCode' in response) {
                if (response.returnCode.startsWith('JWT_ERR')) {
                  errorMessage = 'Erreur d\'authentification lors de l\'upload du fichier.';
                } else if (response.returnCode === 'VIDEO_UPLOAD_ERR_01') {
                  errorMessage = 'Authentification: utilisateur inconnu';
                } else if (response.returnCode === 'VIDEO_UPLOAD_ERR_02') {
                  errorMessage = 'Format de fichier non reconnu.';
                }
              }
            }

            displayDialogError({
              message: errorMessage,
            });

            clearForm()
            resetUpload()
            emits('failed');
          },
          onSuccess: () => {
           
            securityStore.getCsrf(FormNameEnum.PROGRAM_VIDEO)
              .then((csrf) => {
                params.videoFile = undefined
                params.legacyVersion = false
                params.csrf = csrf?.token
                postVideo(params)
              })
          }
        })
        .then((tusUpload: tus.Upload | undefined) => {
          apiUploadController.value = tusUpload

          if (apiUploadController.value != null) {
            startOrResumeUpload(apiUploadController.value)
          }
        })
        .catch(async (error: Error | ApiError | tus.DetailedError) => {
          if (error.message === 'TUS_UPLOAD_NOT_SUPPORTED') {
            securityStore.getCsrf(FormNameEnum.PROGRAM_VIDEO)
              .then((csrf) => {
                // Tus not supported, so upload with Axios
                abortController.value = new AbortController()
                params.legacyVersion = true
                params.abortController = abortController.value
                params.csrf = csrf?.token
                postVideo(params)
              })

          } else {
            logger?.error(error)
          }
        })
    }
  }
}

function postVideo(params: IApiPostVideo) {
  videoRepository
    .postVideo(params)
    .then(() => {
      displayNotifSuccess('La vidéo a été ajoutée avec succès');
      emits('success');
      onClose();
    })
    .catch((error: Error | ApiError) => {
      if (error instanceof ApiError && error.code === ApiErrorLabel.CancelledError) {
        displayNotifWarning('Dépôt de vidéo annulé avec succès');
      } else {
        displayDialogError({
          message: error.message ?? 'null',
        });
      }
      emits('failed');
    })
    .finally(() => {
      clearForm()
      resetUpload();
      loading.value = false;
    });
}

function checkFileType(files) {
  return storeConfig.value?.uploader?.unauthorizedFileTypes == null
    ? files
    : files.filter((file) =>
        storeConfig.value?.uploader?.unauthorizedFileTypes
          ?.split(',')
          .some((ft) => file.name.lastIndexOf(ft) === -1),
      );
}

function setActiveSecondaryUsers() {
  activeSecondaryUsers.value = users.value
    ?.filter((u) => u.status === UserStatusEnum.ACTIVE)
    .map((u) => ({
      label: u.username ?? '',
      value: u,
    }))
    .sort((a, b) =>
      a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase()),
    );
}

/**
 * Watch properties
 */
watch(
  () => show.value,
  (newValue: boolean) => {
    if (newValue === true) {
      activeUsers.value = users.value
        ?.filter((u) => u.status === UserStatusEnum.ACTIVE)
        .map((u) => ({
          label: u.username ?? '',
          value: u,
        }))
        .sort((a, b) =>
          a.label
            .toLocaleLowerCase()
            .localeCompare(b.label.toLocaleLowerCase()),
        );

      setActiveSecondaryUsers();

      selectedFile.value = null;
      selectedPrimaryUser.value = null;
      selectedSecondaryUsers.value = null;

      // Init progress values
      resetUpload();
    }
  },
  { immediate: true },
);

watch(
  () => selectedPrimaryUser.value,
  (newVal: IQSelectOption<IUser> | null) => {
    if (newVal != null) {
      selectedSecondaryUsers.value = null;

      setActiveSecondaryUsers();

      activeSecondaryUsers.value = activeSecondaryUsers.value.filter(
        (o) => o.value?.email !== newVal.value?.email,
      );
    }
  },
);

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_toggle = _resolveComponent("q-toggle")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    "show-dialog": _unref(show),
    title: 'Dépôt de vidéo',
    "subtitle-html": `Programme: <span lang='ja'>${_unref(program).label?.toLocaleUpperCase()}</span>`,
    maximized: _unref(isMobile),
    persistent: false,
    "dialog-size": _unref(DialogSizeEnum).SMALL,
    "loading-first": loading.value,
    onClose: _cache[6] || (_cache[6] = ($event: any) => (onClose()))
  }, {
    content: _withCtx(() => [
      _createVNode(_component_q_card, {
        flat: "",
        class: "no-border-radius q-pt-sm"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, {
            class: "q-pt-xs q-pb-none",
            tabindex: "-1",
            ref: "qSignupDescription"
          }, {
            default: _withCtx(() => [
              _cache[9] || (_cache[9] = _createTextVNode(" Le dépôt de vidéo inclut obligatoirement : ")),
              _createElementVNode("ul", _hoisted_1, [
                _createElementVNode("li", null, " Le dépôt du fichier au format " + _toDisplayString(authorizedFileFormat.value) + " et d'un poids total maximum de " + _toDisplayString(_unref(tailleFichier)(maxFileSize.value)) + ", ", 1 /* TEXT */),
                _cache[7] || (_cache[7] = _createElementVNode("li", null, " Le nom d'utilisateur du pratiquant présent sur la vidéo et ayant un compte sur l'application. ", -1 /* HOISTED */)),
                _cache[8] || (_cache[8] = _createElementVNode("li", null, " Le cas échéant, le nom d'utilisateur du ou des pratiquants accompagnant le pratiquant principal ", -1 /* HOISTED */))
              ])
            ]),
            _: 1 /* STABLE */
          }, 512 /* NEED_PATCH */),
          _createVNode(_component_q_card_section, { class: "q-pa-md" }, {
            default: _withCtx(() => [
              _cache[10] || (_cache[10] = _createElementVNode("div", { class: "q-mx-sm q-mb-sm" }, "* : champs obligatoires", -1 /* HOISTED */)),
              _createCommentVNode(" Fichier vidéo "),
              _createVNode(_unref(QFile), {
                modelValue: selectedFile.value,
                "onUpdate:modelValue": [
                  _cache[1] || (_cache[1] = ($event: any) => ((selectedFile).value = $event)),
                  onUpdateModelValue
                ],
                name: "selected_file",
                ref_key: "videoUploaderRef",
                ref: videoUploaderRef,
                class: "q-mb-sm set-outline",
                outlined: "",
                "stack-label": !_unref(isMobile),
                "aria-required": "true",
                "aria-invalid": selectedFile.value == null,
                label: `* Choisir une vidéo (max: ${_unref(tailleFichier)(maxFileSize.value)}):`,
                counter: "",
                "counter-label": counterLabelFn,
                filter: checkFileType,
                "label-color": _unref($q).dark.isActive ? 'white' : 'black',
                accept: authorizedFileFormat.value,
                "max-file-size": maxFileSize.value,
                "hide-bottom-space": "",
                "lazy-rules": "",
                rules: _unref(videoFileRules),
                onRejected: onRejected,
                disable: loading.value
              }, _createSlots({
                prepend: _withCtx(() => [
                  _createVNode(_component_q_icon, { name: "attach_file" })
                ]),
                append: _withCtx(() => [
                  _createVNode(_component_q_btn, {
                    flat: "",
                    round: "",
                    dense: "",
                    icon: "close",
                    class: "set-outline",
                    title: "Vider le champ vidéo   ",
                    onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (onClearQFile()), ["stop"]))
                  })
                ]),
                _: 2 /* DYNAMIC */
              }, [
                (error.value)
                  ? {
                      name: "error",
                      fn: _withCtx(() => [
                        _createTextVNode(" ERROR : " + _toDisplayString(error.value), 1 /* TEXT */)
                      ]),
                      key: "0"
                    }
                  : undefined
              ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "stack-label", "aria-invalid", "label", "label-color", "accept", "max-file-size", "rules", "disable"]),
              _createCommentVNode(" Pratiquant "),
              _createVNode(_unref(QSelect), {
                modelValue: selectedPrimaryUser.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedPrimaryUser).value = $event)),
                ref_key: "pratiquantSelectRef",
                ref: pratiquantSelectRef,
                options: activeUsers.value,
                name: "selected_user",
                outlined: "",
                "stack-label": !_unref(isMobile),
                "aria-required": "true",
                "aria-invalid": selectedPrimaryUser.value == null,
                class: "q-mb-sm",
                "input-class": "set-outline",
                "label-color": _unref($q).dark.isActive ? 'white' : 'black',
                label: "* Pratiquant présent sur la vidéo :",
                "lazy-rules": "",
                rules: _unref(requiredAtLeastOneDemonstrator),
                "hide-bottom-space": "",
                "popup-content-class": "subjectOption",
                disable: loading.value
              }, {
                option: _withCtx((scope) => [
                  _createVNode(_component_q_item, _mergeProps(scope.itemProps, {
                    class: {
                  'q-item-focused': scope.focused,
                  'q-item-selected': scope.selected && !scope.focused,
                }
                  }), {
                    default: _withCtx(() => [
                      _createVNode(_component_q_item_section, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_item_label, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(scope.selected ? '# ' : '') + _toDisplayString(scope.opt.label), 1 /* TEXT */)
                            ]),
                            _: 2 /* DYNAMIC */
                          }, 1024 /* DYNAMIC_SLOTS */)
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1024 /* DYNAMIC_SLOTS */)
                    ]),
                    _: 2 /* DYNAMIC */
                  }, 1040 /* FULL_PROPS, DYNAMIC_SLOTS */, ["class"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["modelValue", "options", "stack-label", "aria-invalid", "label-color", "rules", "disable"]),
              _createCommentVNode(" Accompagnateur(s) "),
              _createVNode(_unref(QSelect), {
                outlined: "",
                class: "q-mb-sm",
                modelValue: selectedSecondaryUsers.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedSecondaryUsers).value = $event)),
                options: activeSecondaryUsers.value,
                multiple: "",
                "map-options": "",
                "stack-label": !_unref(isMobile),
                "label-color": _unref($q).dark.isActive ? 'white' : 'black',
                label: "Accompagné par (un ou plusieurs pratiquants) :",
                behavior: "menu",
                disable: loading.value
              }, {
                option: _withCtx((scope) => [
                  _createVNode(_component_q_item, _mergeProps(scope.itemProps, {
                    class: {
                  'q-item-focused': scope.focused,
                  'q-item-selected': scope.selected && !scope.focused,
                }
                  }), {
                    default: _withCtx(() => [
                      _createVNode(_component_q_item_section, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_item_label, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(scope.opt.label), 1 /* TEXT */)
                            ]),
                            _: 2 /* DYNAMIC */
                          }, 1024 /* DYNAMIC_SLOTS */)
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1024 /* DYNAMIC_SLOTS */),
                      _createVNode(_component_q_item_section, { side: "" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_toggle, {
                            "model-value": scope.selected,
                            "onUpdate:modelValue": ($event: any) => (scope.toggleOption(scope.opt))
                          }, null, 8 /* PROPS */, ["model-value", "onUpdate:modelValue"])
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1024 /* DYNAMIC_SLOTS */)
                    ]),
                    _: 2 /* DYNAMIC */
                  }, 1040 /* FULL_PROPS, DYNAMIC_SLOTS */, ["class"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["modelValue", "options", "stack-label", "label-color", "disable"]),
              _createCommentVNode(" Upload progress bar "),
              _createVNode(QProgressBar, {
                "pb-description": 'Progression du dépôt de la vidéo :',
                "pb-show-description": true,
                "pb-current-value": loaded.value ?? 0,
                "pb-max": total.value,
                "is-uploader": true
              }, null, 8 /* PROPS */, ["pb-current-value", "pb-max"])
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_q_card, {
        flat: "",
        class: "no-border-radius"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_separator),
          _createVNode(_component_q_card_actions, { align: "right" }, {
            default: _withCtx(() => [
              _createVNode(QBtnCancel, {
                label: "Annuler",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (onClose()))
              }),
              _createCommentVNode(" <QBtnCancel\n            label=\"Pause\"\n            @click=\"onPauseUpload()\"\n            :disable=\"abortController != null\"\n          /> "),
              _createVNode(QBtnValid, {
                label: 'Déposer la vidéo',
                "loading-label": 'Chargement',
                type: 'submit',
                loading: loading.value,
                onClick: _cache[5] || (_cache[5] = ($event: any) => (onValidate()))
              }, null, 8 /* PROPS */, ["loading"])
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["show-dialog", "subtitle-html", "maximized", "dialog-size", "loading-first"]))
}
}

})